import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Trash2, Loader2, Send } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "./AuthModal";
import ReportButton from "./ReportButton";

const ago = (iso) => {
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60) || 1}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const Avatar = ({ name, picture }) => picture
  ? <img src={picture} alt="" className="w-9 h-9 rounded-full shrink-0" />
  : <span className="w-9 h-9 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-sm font-bold text-white shrink-0">{(name || "?")[0].toUpperCase()}</span>;

export default function Comments({ slug }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => { api.get(`/news/${slug}/comments`).then((r) => setItems(r.data.items)).catch(() => {}); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/news/${slug}/comments`, { text }); setItems([r.data, ...items]); setText(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not post comment"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/comments/${id}`); setItems(items.filter((c) => c.id !== id)); } catch { toast.error("Delete failed"); }
  };

  return (
    <section className="mt-12" data-testid="comments-section">
      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-5"><MessageSquare size={18} /> Comments <span className="text-white/40 font-normal" data-testid="comments-count">({items.length})</span></h3>
      {user ? (
        <form onSubmit={submit} className="flex gap-3 mb-8">
          <Avatar name={user.name} picture={user.picture} />
          <div className="flex-1">
            <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={3} placeholder="Share your thoughts…" data-testid="comment-input"
              className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))] resize-none" />
            <div className="flex justify-between items-center mt-2">
              <span className="text-[11px] text-white/30">{text.length}/2000</span>
              <button type="submit" disabled={busy || !text.trim()} data-testid="comment-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="card-surface rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-white/60">Sign in to join the conversation.</span>
          <button onClick={() => setAuthOpen(true)} data-testid="comment-signin-btn" className="px-4 py-2 rounded-full bg-white text-black text-sm font-bold">Sign In to Comment</button>
        </div>
      )}
      <div className="space-y-5">
        {items.map((c) => (
          <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            <Avatar name={c.user_name} picture={c.user_picture} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm"><span className="font-semibold text-white">{c.user_name}</span><span className="text-white/30 text-xs">{ago(c.created_at)}</span>
                {(user?.is_admin || user?.user_id === c.user_id) && <button onClick={() => remove(c.id)} className="ml-auto text-white/30 hover:text-red-400" data-testid={`comment-delete-${c.id}`}><Trash2 size={13} /></button>}
                {user?.user_id !== c.user_id && <ReportButton type="comment" id={c.id} ownerId={c.user_id} iconOnly className={`${user?.is_admin ? "" : "ml-auto"} text-white/30`} />}
              </div>
              <p className="text-sm text-white/75 mt-1 whitespace-pre-line break-words">{c.text}</p>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-sm text-white/40" data-testid="comments-empty">No comments yet. Be the first!</div>}
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </section>
  );
}

export function ShareBar({ title }) {
  const url = window.location.href;
  const enc = encodeURIComponent;
  const links = [
    ["WhatsApp", `https://wa.me/?text=${enc(`${title} ${url}`)}`],
    ["X", `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`],
    ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
    ["Telegram", `https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`],
  ];
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  const native = () => navigator.share?.({ title, url }).catch(() => {});
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-bar">
      <span className="eyebrow text-white/40 mr-1">Share</span>
      {links.map(([n, href]) => <a key={n} href={href} target="_blank" rel="noopener noreferrer" data-testid={`share-${n.toLowerCase()}`} className="px-3 py-1.5 rounded-full card-surface text-xs font-semibold text-white/70 hover:text-white hover:border-white/30 transition-colors">{n}</a>)}
      <button onClick={copy} data-testid="share-copy" className="px-3 py-1.5 rounded-full card-surface text-xs font-semibold text-white/70 hover:text-white">Copy link</button>
      {typeof navigator !== "undefined" && navigator.share && <button onClick={native} className="px-3 py-1.5 rounded-full card-surface text-xs font-semibold text-white/70 hover:text-white">More…</button>}
    </div>
  );
}

export function ReadMore({ items }) {
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 md:px-6 pb-16" data-testid="read-more">
      <div className="flex items-end justify-between mb-5"><h3 className="text-2xl font-bold text-white">Read More</h3><Link to="/news" className="text-sm font-semibold text-white/60 hover:text-white">All news →</Link></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((a) => (
          <Link key={a.id} to={`/news/${a.slug}`} className="group card-surface rounded-xl overflow-hidden hover-rise" data-testid={`read-more-${a.slug}`}>
            <div className="img-zoom aspect-[16/10]"><img src={mediaUrl(a.cover)} alt={a.title} className="w-full h-full object-cover" loading="lazy" /></div>
            <div className="p-4"><span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>{a.category}</span><h4 className="mt-1.5 font-bold text-white leading-snug line-clamp-2 group-hover:underline">{a.title}</h4></div>
          </Link>
        ))}
      </div>
    </section>
  );
}
